import { useState, useCallback } from 'react';
import { mobileAPI } from '../../api/MobileAPI';
import { inferCountry } from '../../services/utils/inferCountry';
function mergeCountyTimesStores(base, extra) {
    const merged = { ...base };
    for (const county of Object.keys(extra)) {
        merged[county] = {
            countyName: extra[county].countyName,
            year: extra[county].year,
            country: extra[county].country,
            times: { ...(merged[county]?.times ?? {}), ...extra[county].times },
        };
    }
    return merged;
}
async function loadDefaultCountyTimesFromServer() {
    try {
        const res = await fetch('/api/standards');
        if (!res.ok) {
            console.warn('Default county times load failed, status:', res.status);
            return undefined;
        }
        const items = await res.json();
        const currentYear = new Date().getFullYear();
        const store = {};
        for (const item of items) {
            if (!item.countyName || !item.times)
                continue;
            store[item.countyName] = {
                countyName: item.countyName,
                year: item.year ?? currentYear,
                country: item.country ?? inferCountry(item.countyName),
                times: item.times,
            };
        }
        return store;
    }
    catch (error) {
        console.warn('Default county times load failed:', error);
        return undefined;
    }
}
async function loadDefaultCountyTimesFromFile() {
    try {
        const res = await fetch('/assets/json/county-times.json');
        if (!res.ok)
            return {};
        const raw = await res.json();
        const currentYear = new Date().getFullYear();
        const store = {};
        for (const [name, times] of Object.entries(raw)) {
            store[name] = { countyName: name, year: currentYear, country: inferCountry(name), times: times };
        }
        return store;
    }
    catch (error) {
        console.warn('Default county times load failed:', error);
        return {};
    }
}
export function useCountyStandards({ showToast }) {
    const [countyTimesStore, setCountyTimesStore] = useState({});
    const [defaultStandardNames, setDefaultStandardNames] = useState(new Set());
    const [pendingLoad, setPendingLoad] = useState(null);
    const [activeStandards, setActiveStandards] = useState([]);
    const [selectedStrokes, setSelectedStrokes] = useState([]);
    const [selectedDistances, setSelectedDistances] = useState([]);
    const loadCountyTimesStore = useCallback(async () => {
        try {
            let defaultStore = await loadDefaultCountyTimesFromServer();
            if (!defaultStore) {
                console.debug('loadCountyTimesStore: unable to get defaults from server. Defaulting to file.');
                defaultStore = await loadDefaultCountyTimesFromFile();
            }
            setDefaultStandardNames(new Set(Object.keys(defaultStore)));
            const stored = await mobileAPI.loadCountyTimesStore();
            const merged = mergeCountyTimesStores(defaultStore, stored);
            console.debug('loadCountyTimesStore: default count', Object.keys(defaultStore).length, 'stored count', Object.keys(stored).length, 'merged count', Object.keys(merged).length);
            if (Object.keys(merged).length > 0)
                setCountyTimesStore(merged);
        }
        catch (error) {
            console.warn('County times store load failed:', error);
        }
    }, []);
    const loadActiveStandards = useCallback(async () => {
        try {
            const active = await mobileAPI.loadActiveStandards();
            setActiveStandards(active);
        }
        catch (error) {
            console.warn('Active standards load failed:', error);
            setActiveStandards([]);
        }
    }, []);
    const loadSelectedStrokes = useCallback(async () => {
        try {
            const strokes = await mobileAPI.loadSelectedStrokes();
            setSelectedStrokes(strokes);
        }
        catch (error) {
            console.warn('Selected strokes load failed:', error);
            setSelectedStrokes([]);
        }
    }, []);
    const loadSelectedDistances = useCallback(async () => {
        try {
            const distances = await mobileAPI.loadSelectedDistances();
            setSelectedDistances(distances);
        }
        catch (error) {
            console.warn('Selected distances load failed:', error);
            setSelectedDistances([]);
        }
    }, []);
    const handleActiveStandardsChange = useCallback(async (active) => {
        setActiveStandards(active);
        try {
            await mobileAPI.saveActiveStandards(active);
        }
        catch (error) {
            console.error('Failed to save active standards:', error);
            showToast('Failed to save standards selection', 'error');
        }
    }, [showToast]);
    const handleSelectedStrokesChange = useCallback(async (strokes) => {
        setSelectedStrokes(strokes);
        try {
            await mobileAPI.saveSelectedStrokes(strokes);
        }
        catch (error) {
            console.error('Failed to save selected strokes:', error);
            showToast('Failed to save stroke filters', 'error');
        }
    }, [showToast]);
    const handleSelectedDistancesChange = useCallback(async (distances) => {
        setSelectedDistances(distances);
        try {
            await mobileAPI.saveSelectedDistances(distances);
        }
        catch (error) {
            console.error('Failed to save selected distances:', error);
            showToast('Failed to save distance filters', 'error');
        }
    }, [showToast]);
    const pickAndLoadCountyTimes = useCallback(async () => {
        try {
            const results = await mobileAPI.pickCountyTimesFile();
            if (results === null)
                return;
            const currentYear = new Date().getFullYear();
            setPendingLoad(results.map(r => {
                const defaultStandardName = r.countyName.replace(/\s+\d{4}$/, '').trim();
                return {
                    countyName: r.countyName,
                    standardName: defaultStandardName,
                    times: r.times,
                    year: currentYear,
                    country: inferCountry(r.countyName),
                    fileName: r.fileName,
                };
            }));
        }
        catch (error) {
            showToast(`Failed to load standards: ${error.message}`, 'error');
        }
    }, [showToast]);
    const confirmLoad = useCallback(() => {
        if (!pendingLoad)
            return;
        console.debug('confirmLoad: importing custom standards', pendingLoad.map(r => `${r.standardName} ${r.year}`));
        setCountyTimesStore(prev => {
            const next = { ...prev };
            for (const { standardName, times, year, country } of pendingLoad) {
                const key = `${standardName.trim()} ${year}`;
                next[key] = { countyName: standardName.trim(), year, country, times: { ...(next[key]?.times ?? {}), ...times } };
            }
            mobileAPI.saveCountyTimesStore(next);
            return next;
        });
        showToast(`Loaded: ${pendingLoad.map(r => `${r.standardName} ${r.year}`).join(', ')}`);
        setPendingLoad(null);
    }, [pendingLoad, showToast]);
    const clearOneCounty = useCallback(async (county) => {
        setCountyTimesStore(prev => {
            const next = { ...prev };
            delete next[county];
            mobileAPI.saveCountyTimesStore(next);
            return next;
        });
        showToast(`Cleared ${county}`);
    }, [showToast]);
    return {
        countyTimesStore,
        setCountyTimesStore,
        defaultStandardNames,
        pendingLoad,
        setPendingLoad,
        activeStandards,
        selectedStrokes,
        selectedDistances,
        loadCountyTimesStore,
        loadActiveStandards,
        loadSelectedStrokes,
        loadSelectedDistances,
        handleActiveStandardsChange,
        handleSelectedStrokesChange,
        handleSelectedDistancesChange,
        pickAndLoadCountyTimes,
        confirmLoad,
        clearOneCounty,
    };
}
